import { esc } from "../utils/html";

/** Suchfeld mit Vorschlagsliste darunter, im Gewand von initDropdownCheck.
 *  Erwartetes Markup:
 *    <div class="dropdown-check suggest-input">
 *      <input type="search" />
 *      <div class="dropdown-check-panel dropdown-check-panel-left"></div>
 *    </div>
 *  Beim Tippen erscheinen alle Eintraege, deren Beschriftung die Eingabe enthaelt.
 *  Erst die Auswahl eines Eintrags meldet sich per onSelect; jede spaetere Aenderung
 *  am Text hebt sie wieder auf (onSelect(null)).
 */

export interface SuggestItem {
  value: string;
  label: string;
  /** Grauer Zusatz hinter der Beschriftung, etwa zum Unterscheiden gleicher Namen */
  sub?: string;
}

export interface SuggestInputOptions {
  /** Wird bei jeder Eingabe neu abgefragt, damit die Liste den aktuellen Filtern folgt */
  items: () => SuggestItem[];
  onSelect: (value: string | null) => void;
}

export interface SuggestInputHandle {
  /** Leert Feld und Auswahl. Loest bewusst kein onSelect aus. */
  clear(): void;
  getValue(): string | null;
}

export function initSuggestInput(container: HTMLElement, opts: SuggestInputOptions): SuggestInputHandle {
  const input = container.querySelector("input") as HTMLInputElement;
  const panel = container.querySelector(".dropdown-check-panel") as HTMLElement;
  let selected: string | null = null;
  let matches: SuggestItem[] = [];
  let focused = 0;

  function close() { container.classList.remove("open"); }

  function highlight(label: string, q: string): string {
    const i = label.toLowerCase().indexOf(q);
    if (i < 0) return esc(label);
    return `${esc(label.slice(0, i))}<strong>${esc(label.slice(i, i + q.length))}</strong>${esc(label.slice(i + q.length))}`;
  }

  function showSuggestions() {
    const q = input.value.trim().toLowerCase();
    if (!q) { matches = []; close(); return; }
    matches = opts.items().filter(it => it.label.toLowerCase().includes(q));
    focused = 0;
    panel.innerHTML = matches.length
      ? matches.map((it, i) => `<div class="dropdown-check-item suggest-item${i === focused ? " is-focused" : ""}" data-index="${i}">
          <span>${highlight(it.label, q)}</span>${it.sub ? `<span class="suggest-sub">${esc(it.sub)}</span>` : ""}
        </div>`).join("")
      : `<div class="suggest-empty">Keine Treffer</div>`;
    document.querySelectorAll(".dropdown-check.open").forEach(d => { if (d !== container) d.classList.remove("open"); });
    container.classList.add("open");
  }

  function moveFocus(delta: number) {
    if (!matches.length) return;
    focused = (focused + delta + matches.length) % matches.length;
    const items = Array.from(panel.querySelectorAll(".suggest-item")) as HTMLElement[];
    items.forEach((el, i) => el.classList.toggle("is-focused", i === focused));
    items[focused]?.scrollIntoView({ block: "nearest" });
  }

  function choose(it: SuggestItem) {
    input.value = it.label;
    selected = it.value;
    close();
    opts.onSelect(it.value);
  }

  input.addEventListener("input", () => {
    if (selected !== null) { selected = null; opts.onSelect(null); }
    showSuggestions();
  });

  // Nach dem Wegklicken die Liste beim erneuten Hineinklicken wieder zeigen, solange nichts gewaehlt ist
  input.addEventListener("focus", () => { if (selected === null) showSuggestions(); });

  input.addEventListener("keydown", (e) => {
    const open = container.classList.contains("open");
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) { if (selected === null) showSuggestions(); return; }
      moveFocus(e.key === "ArrowDown" ? 1 : -1);
    } else if (e.key === "Enter") {
      if (open && matches[focused]) { e.preventDefault(); choose(matches[focused]); }
    } else if (e.key === "Escape") {
      // Escape leert bei type="search" sonst das Feld; bei offener Liste nur schliessen
      if (open) { e.preventDefault(); close(); }
    }
  });

  // Per Tab verlassen schliesst ebenfalls
  container.addEventListener("focusout", (e) => {
    if (!container.contains(e.relatedTarget as Node | null)) close();
  });

  container.addEventListener("click", (e) => e.stopPropagation());

  panel.addEventListener("click", (e) => {
    const item = (e.target as HTMLElement).closest(".suggest-item") as HTMLElement | null;
    if (!item) return;
    const it = matches[+(item.dataset.index ?? -1)];
    if (it) choose(it);
  });

  // Ohne mousedown-Abfang verliert das Feld beim Klick in die Liste den Fokus,
  // focusout schliesst sie und der Klick ginge ins Leere.
  panel.addEventListener("mousedown", (e) => e.preventDefault());

  return {
    clear() { input.value = ""; selected = null; matches = []; close(); },
    getValue() { return selected; },
  };
}
